/**
 * 塩基ビューポート（§0.3.1 / §0.3.4・U149 / U156 / U157 / U158 / U165 / U166）。
 *
 * - Canvas は `devicePixelRatio` に追従（文字がぼやけない）
 * - 1 行の塩基数はビューポート幅から決める（10 bp ブロックは維持）
 * - 可視範囲＋前後のチャンクだけ読む（染色体級でも一定時間）
 * - 既定は 1 本鎖。オプションで相補鎖を下段に出す
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CODE_TO_CHAR,
  ChunkedSequence,
  baseColorTable,
  complementCode,
  computeSequenceLayout,
  formatBase1Based,
  type Interval,
  type ViewRange,
} from '@kensuke/genome-core';
import type { SequenceOverlayMark } from './geneAnalysisAnalysis';
import type { GeneAnalysisDisplayOptions } from './useGeneAnalysisWorkspace';
import {
  MAX_SEQUENCE_FONT_SIZE,
  MIN_SEQUENCE_FONT_SIZE,
  visibleLineCount,
} from './geneAnalysisLayout';

const GUTTER_WIDTH = 104;
const PADDING_X = 12;
const PADDING_TOP = 10;
const ROW_GAP = 8;
/** 可視範囲の前後に先読みする塩基数 */
const PREFETCH_BASES = 200_000;

export interface SequenceCanvasProps {
  sequence: ChunkedSequence;
  view: ViewRange;
  onViewChange: (view: ViewRange) => void;
  selection: Interval | null;
  onSelectionChange: (selection: Interval | null) => void;
  display: GeneAnalysisDisplayOptions;
  onFontSizeChange: (fontSize: number) => void;
  /** 実際に描いている範囲。ステータス・マップと表示を合わせるために親へ返す（U159 / U160） */
  onWindowChange?: (window: ViewRange) => void;
  overlays?: SequenceOverlayMark[];
}

interface Size {
  width: number;
  height: number;
}

export default function SequenceCanvas({
  sequence,
  view,
  onViewChange,
  selection,
  onSelectionChange,
  display,
  onFontSizeChange,
  onWindowChange,
  overlays = [],
}: SequenceCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragAnchorRef = useRef<number | null>(null);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const [pixelRatio, setPixelRatio] = useState(() =>
    typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1
  );
  const [loadToken, setLoadToken] = useState(0);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setSize({ width: rect.width, height: rect.height });
    });
    observer.observe(element);
    setSize({ width: element.clientWidth, height: element.clientHeight });
    return () => observer.disconnect();
  }, []);

  // ブラウザズームでも DPR が変わる（U156）
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const update = () => setPixelRatio(window.devicePixelRatio || 1);
    const media = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    media.addEventListener?.('change', update);
    window.addEventListener('resize', update);
    return () => {
      media.removeEventListener?.('change', update);
      window.removeEventListener('resize', update);
    };
  }, [pixelRatio]);

  const layout = useMemo(
    () =>
      computeSequenceLayout({
        viewportWidth: size.width,
        fontSize: display.fontSize,
        gutterWidth: GUTTER_WIDTH,
        paddingX: PADDING_X,
        devicePixelRatio: pixelRatio,
      }),
    [size.width, display.fontSize, pixelRatio]
  );

  const rowHeight = useMemo(() => {
    const strandRows = display.strandMode === 'double' ? 2 : 1;
    return Math.round(display.fontSize * 1.35 * strandRows + ROW_GAP);
  }, [display.fontSize, display.strandMode]);

  const rowCount = useMemo(
    () => visibleLineCount(size.height - PADDING_TOP * 2, rowHeight),
    [size.height, rowHeight]
  );

  /** 行は分子先頭から `basesPerLine` 刻みで揃える（座標が読みやすい） */
  const visibleWindow = useMemo(() => {
    const lineStart = Math.floor(Math.max(0, view.start) / layout.basesPerLine) * layout.basesPerLine;
    const end = Math.min(sequence.length, lineStart + rowCount * layout.basesPerLine);
    return { start: lineStart, end };
  }, [view.start, layout.basesPerLine, rowCount, sequence.length]);

  useEffect(() => {
    if (visibleWindow.end <= visibleWindow.start) return;
    onWindowChange?.(visibleWindow);
  }, [onWindowChange, visibleWindow]);

  const codes = useMemo(() => {
    if (visibleWindow.end <= visibleWindow.start) return new Uint8Array(0);
    return sequence.readSync(visibleWindow);
    // loadToken は先読み完了で再評価するためだけに依存させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequence, visibleWindow.start, visibleWindow.end, loadToken]);

  useEffect(() => {
    if (codes !== null || visibleWindow.end <= visibleWindow.start) return;
    let cancelled = false;
    sequence
      .ensure(visibleWindow, PREFETCH_BASES)
      .then(() => {
        if (!cancelled) setLoadToken((token) => token + 1);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [codes, sequence, visibleWindow]);

  const colorTable = useMemo(
    () => baseColorTable({ theme: display.theme, guanine: display.guanine, contrast: display.contrast }),
    [display.theme, display.guanine, display.contrast]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width <= 0 || size.height <= 0) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = Math.round(size.width * pixelRatio);
    canvas.height = Math.round(size.height * pixelRatio);
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

    const isDark = display.theme === 'dark';
    context.clearRect(0, 0, size.width, size.height);
    context.fillStyle = isDark ? '#0F172A' : '#FFFFFF';
    context.fillRect(0, 0, size.width, size.height);

    const font = `${display.fontSize}px ui-monospace, "SFMono-Regular", "Menlo", "Consolas", monospace`;
    context.font = font;
    context.textBaseline = 'top';

    if (!codes) {
      context.fillStyle = isDark ? '#94A3B8' : '#64748B';
      context.fillText('配列を読み込んでいます…', PADDING_X, PADDING_TOP);
      return;
    }

    const baseX = (indexInLine: number) => {
      const block = Math.floor(indexInLine / layout.blockSize);
      const withinBlock = indexInLine % layout.blockSize;
      return (
        GUTTER_WIDTH +
        PADDING_X +
        block * (layout.blockSize * layout.baseWidth + layout.blockGap) +
        withinBlock * layout.baseWidth
      );
    };

    const strandRowHeight = display.fontSize * 1.35;
    const totalBases = visibleWindow.end - visibleWindow.start;
    const lines = Math.ceil(totalBases / layout.basesPerLine);

    // 選択の背景（塩基色は文字色として使う・§0.3.4）
    if (selection && selection.end > selection.start) {
      context.fillStyle = isDark ? 'rgba(56,189,248,0.22)' : 'rgba(37,99,235,0.16)';
      // 環状ラップ選択（end > length）は尾＋頭の 2 区間として塗る
      const selectionSpans =
        selection.end > sequence.length
          ? [
              { start: selection.start, end: sequence.length },
              { start: 0, end: selection.end - sequence.length },
            ]
          : [{ start: selection.start, end: selection.end }];
      for (const span of selectionSpans) {
        for (let line = 0; line < lines; line += 1) {
          const lineStart = visibleWindow.start + line * layout.basesPerLine;
          const lineEnd = Math.min(lineStart + layout.basesPerLine, visibleWindow.end);
          const from = Math.max(span.start, lineStart);
          const to = Math.min(span.end, lineEnd);
          if (to <= from) continue;
          const y = PADDING_TOP + line * rowHeight;
          const x = baseX(from - lineStart);
          const width = baseX(to - 1 - lineStart) + layout.baseWidth - x;
          context.fillRect(x, y - 2, width, strandRowHeight * (display.strandMode === 'double' ? 2 : 1) + 4);
        }
      }
    }

    for (const mark of overlays) {
      const spans =
        mark.end > sequence.length
          ? [
              { start: mark.start, end: sequence.length },
              { start: 0, end: mark.end - sequence.length },
            ]
          : [{ start: mark.start, end: mark.end }];
      context.fillStyle = mark.active ? `${mark.color}55` : `${mark.color}33`;
      context.strokeStyle = mark.color;
      for (const span of spans) {
        for (let line = 0; line < lines; line += 1) {
          const lineStart = visibleWindow.start + line * layout.basesPerLine;
          const lineEnd = Math.min(lineStart + layout.basesPerLine, visibleWindow.end);
          const from = Math.max(span.start, lineStart);
          const to = Math.min(span.end, lineEnd);
          if (to <= from) continue;
          const y = PADDING_TOP + line * rowHeight;
          const x = baseX(from - lineStart);
          const width = Math.max(1, baseX(to - 1 - lineStart) + layout.baseWidth - x);
          if (mark.kind === 'cut') {
            context.beginPath();
            context.moveTo(x, y - 2);
            context.lineTo(x, y + strandRowHeight + 2);
            context.stroke();
          } else {
            context.fillRect(x, y - 2, width, strandRowHeight + 4);
          }
        }
      }
    }

    // 座標ラベル（U158）
    context.fillStyle = isDark ? '#94A3B8' : '#64748B';
    context.textAlign = 'right';
    for (let line = 0; line < lines; line += 1) {
      const lineStart = visibleWindow.start + line * layout.basesPerLine;
      context.fillText(formatBase1Based(lineStart), GUTTER_WIDTH, PADDING_TOP + line * rowHeight);
    }
    context.textAlign = 'left';

    // 塩基（色ごとにまとめて描き、状態変更を減らす）
    for (let code = 0; code < colorTable.length; code += 1) {
      context.fillStyle = colorTable[code];
      const char = CODE_TO_CHAR[code];
      for (let offset = 0; offset < codes.length; offset += 1) {
        if (codes[offset] !== code) continue;
        const line = Math.floor(offset / layout.basesPerLine);
        if (line >= lines) break;
        const indexInLine = offset % layout.basesPerLine;
        context.fillText(char, baseX(indexInLine), PADDING_TOP + line * rowHeight);
      }
    }

    // 相補鎖（U166）
    if (display.strandMode === 'double') {
      for (let code = 0; code < colorTable.length; code += 1) {
        context.fillStyle = colorTable[code];
        context.globalAlpha = 0.72;
        const char = CODE_TO_CHAR[code];
        for (let offset = 0; offset < codes.length; offset += 1) {
          if (complementCode(codes[offset]) !== code) continue;
          const line = Math.floor(offset / layout.basesPerLine);
          if (line >= lines) break;
          const indexInLine = offset % layout.basesPerLine;
          context.fillText(char, baseX(indexInLine), PADDING_TOP + line * rowHeight + strandRowHeight);
        }
      }
      context.globalAlpha = 1;
    }
  }, [
    codes,
    colorTable,
    display.fontSize,
    display.strandMode,
    display.theme,
    layout,
    pixelRatio,
    rowHeight,
    selection,
    size.height,
    size.width,
    visibleWindow,
    overlays,
    sequence.length,
  ]);

  const baseAtPointer = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>): number | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const line = Math.floor((y - PADDING_TOP) / rowHeight);
      if (line < 0) return null;

      const blockWidth = layout.blockSize * layout.baseWidth + layout.blockGap;
      const relativeX = x - GUTTER_WIDTH - PADDING_X;
      if (relativeX < 0) return null;
      const block = Math.floor(relativeX / blockWidth);
      const withinBlock = Math.min(
        layout.blockSize - 1,
        Math.floor((relativeX - block * blockWidth) / layout.baseWidth)
      );
      const indexInLine = block * layout.blockSize + withinBlock;
      if (indexInLine >= layout.basesPerLine) return null;

      const base = visibleWindow.start + line * layout.basesPerLine + indexInLine;
      return base >= 0 && base < sequence.length ? base : null;
    },
    [layout, rowHeight, sequence.length, visibleWindow.start]
  );

  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const base = baseAtPointer(event);
    if (base === null) return;
    dragAnchorRef.current = base;
    onSelectionChange({ start: base, end: base + 1 });
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const anchor = dragAnchorRef.current;
    if (anchor === null) return;
    const base = baseAtPointer(event);
    if (base === null) return;
    onSelectionChange({ start: Math.min(anchor, base), end: Math.max(anchor, base) + 1 });
  };

  const endDrag = () => {
    dragAnchorRef.current = null;
  };


  // Ctrl+ホイールはブラウザズームを抑止する（passive: false が必要）
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const next = display.fontSize + (event.deltaY < 0 ? 1 : -1);
        onFontSizeChange(
          Math.min(MAX_SEQUENCE_FONT_SIZE, Math.max(MIN_SEQUENCE_FONT_SIZE, next))
        );
        return;
      }
      event.preventDefault();
      const lines = event.deltaY > 0 ? 3 : -3;
      const delta = lines * layout.basesPerLine;
      const span = view.end - view.start;
      const start = Math.min(Math.max(0, view.start + delta), Math.max(0, sequence.length - span));
      onViewChange({ start, end: start + span });
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [display.fontSize, layout.basesPerLine, onFontSizeChange, onViewChange, sequence.length, view.end, view.start]);

  return (
    <div
      ref={containerRef}
      className="ga-sequence-canvas"
      data-testid="gene-analysis-sequence-canvas"
      // 受け入れ検証（U157 / U164）で 1 画面の可視塩基数を機械的に読むための計測値
      data-bases-per-line={layout.basesPerLine}
      data-visible-bases={Math.max(0, visibleWindow.end - visibleWindow.start)}
      data-device-pixel-ratio={pixelRatio}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        
      />
    </div>
  );
}
